import type { BannerCardapio, DestinoBanner, GrupoCatalogo } from '../api'

/** Mesmo limite que o servidor aceita. */
export const MAX_BANNERS = 8

/** Destino em edição no admin: produto ou categoria ainda não escolhidos ficam com o id nulo. */
export type DestinoBannerRascunho =
  | { tipo: 'NENHUM' }
  | { tipo: 'PRODUTO'; idProduto: number | null }
  | { tipo: 'GRUPO'; idGrupo: number | null }
  | { tipo: 'PROMOCOES' }

export type BannerRascunho = Omit<BannerCardapio, 'destino'> & { destino: DestinoBannerRascunho }

interface CatalogoAgora {
  idsGrupos: Set<number>
  idsProdutos: Set<number>
  qtdPromocoes: number
}

function destinoExiste(destino: DestinoBanner, catalogo: CatalogoAgora): boolean {
  switch (destino.tipo) {
    case 'NENHUM':
      return true
    case 'PRODUTO':
      return catalogo.idsProdutos.has(destino.idProduto)
    case 'GRUPO':
      return catalogo.idsGrupos.has(destino.idGrupo)
    case 'PROMOCOES':
      return catalogo.qtdPromocoes > 0
    default:
      // Destino que este front ainda não conhece: sem saber abrir, é melhor não mostrar.
      return false
  }
}

/** Banner que leva a um lugar que não existe agora neste canal não aparece; o sem link sempre aparece. */
export function bannersDisponiveis(
  banners: BannerCardapio[] | null | undefined,
  grupos: GrupoCatalogo[],
  qtdPromocoes: number,
): BannerCardapio[] {
  if (!banners?.length) return []

  const catalogo: CatalogoAgora = {
    idsGrupos: new Set(grupos.map((g) => g.id)),
    idsProdutos: new Set(grupos.flatMap((g) => g.produtos.map((p) => p.id))),
    qtdPromocoes,
  }

  return banners.filter((banner) => destinoExiste(banner.destino, catalogo))
}

/** Data do navegador em 'YYYY-MM-DD'. Só serve para aviso no admin: quem decide o período é o servidor, no fuso da loja. */
export function dataDeHoje(agora: Date = new Date()): string {
  const doisDigitos = (n: number): string => String(n).padStart(2, '0')
  return `${agora.getFullYear()}-${doisDigitos(agora.getMonth() + 1)}-${doisDigitos(agora.getDate())}`
}

/** Datas no formato 'YYYY-MM-DD' comparam certo como texto. */
export function fimAntesDoInicio(banner: Pick<BannerRascunho, 'inicio' | 'fim'>): boolean {
  return Boolean(banner.inicio && banner.fim && banner.fim < banner.inicio)
}

/** O que falta neste banner para poder salvar, já no texto da barra; `null` quando está completo. */
function oQueFalta(banner: BannerRascunho): string | null {
  if (banner.destino.tipo === 'PRODUTO' && typeof banner.destino.idProduto !== 'number') {
    return 'escolha o produto que ele abre.'
  }
  if (banner.destino.tipo === 'GRUPO' && typeof banner.destino.idGrupo !== 'number') {
    return 'escolha o grupo que ele abre.'
  }
  if (fimAntesDoInicio(banner)) {
    return 'o "Mostrar até" está antes do "Mostrar de". Corrija uma das datas.'
  }
  return null
}

/** Como o lojista reconhece o banner: pelo nome, entre aspas, ou pela posição na lista. */
export function referenciaBanner(banner: Pick<BannerRascunho, 'nome'>, numero: number): string {
  const nome = banner.nome?.trim()
  return nome ? `"${nome}"` : String(numero)
}

export interface ProblemaBanner {
  idBanner: string
  mensagem: string
}

/** O primeiro banner que impede de salvar e o que dizer ao lojista; `null` quando está tudo certo. */
export function problemaNosBanners(banners: BannerRascunho[] | null | undefined): ProblemaBanner | null {
  for (const [indice, banner] of (banners ?? []).entries()) {
    const falta = oQueFalta(banner)
    if (falta) return { idBanner: banner.id, mensagem: `Banner ${referenciaBanner(banner, indice + 1)}: ${falta}` }
  }

  return null
}

/** Etiqueta do banner no admin; quando valem várias, fica a primeira desta lista. */
export type SituacaoBanner = 'FALTA_COMPLETAR' | 'NAO_APARECE' | 'ENCERRADO' | 'AGENDADO' | 'NO_AR'

/** O que o admin sabe agora dos produtos e grupos, pelo recorte do catálogo. */
export interface CatalogoDoAdmin {
  produtos: ReadonlyMap<number, { pausado: boolean; oculto: boolean }>
  grupos: ReadonlyMap<number, { oculto: boolean }>
}

function destinoForaDoCardapio(destino: DestinoBannerRascunho, catalogo: CatalogoDoAdmin): boolean {
  if (destino.tipo === 'PRODUTO' && destino.idProduto !== null) {
    const produto = catalogo.produtos.get(destino.idProduto)
    return !produto || produto.pausado || produto.oculto
  }
  if (destino.tipo === 'GRUPO' && destino.idGrupo !== null) {
    const grupo = catalogo.grupos.get(destino.idGrupo)
    return !grupo || grupo.oculto
  }
  return false
}

/** `null`: produto ou grupo escolhido sem o catálogo (carregando ou com erro), então não dá para saber. Promoções ficam "No ar": o admin não sabe se há promoção agora. */
export function situacaoBanner(banner: BannerRascunho, catalogo: CatalogoDoAdmin | null, hoje: string): SituacaoBanner | null {
  if (oQueFalta(banner)) return 'FALTA_COMPLETAR'
  if (banner.destino.tipo === 'PRODUTO' || banner.destino.tipo === 'GRUPO') {
    if (!catalogo) return null
    if (destinoForaDoCardapio(banner.destino, catalogo)) return 'NAO_APARECE'
  }
  if (banner.fim && banner.fim < hoje) return 'ENCERRADO'
  if (banner.inicio && banner.inicio > hoje) return 'AGENDADO'
  return 'NO_AR'
}

/** 'YYYY-MM-DD' em 'DD/MM', com o ano só quando não é o de hoje. Pela string: `new Date` cairia em UTC e voltaria um dia. */
function diaMes(data: string, hoje: string): string {
  const [ano, mes, dia] = data.split('-')
  return ano === hoje.slice(0, 4) ? `${dia}/${mes}` : `${dia}/${mes}/${ano}`
}

/** Período do banner em poucas palavras, para a linha de resumo. */
export function resumoPeriodo(banner: Pick<BannerRascunho, 'inicio' | 'fim'>, hoje: string): string {
  if (banner.inicio && banner.fim) {
    const periodo = `de ${diaMes(banner.inicio, hoje)} a ${diaMes(banner.fim, hoje)}`
    return fimAntesDoInicio(banner) ? `datas trocadas (${periodo})` : periodo
  }
  if (banner.inicio) return `a partir de ${diaMes(banner.inicio, hoje)}`
  if (banner.fim) return `até ${diaMes(banner.fim, hoje)}`
  return 'sem data'
}
